// User and Authentication types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  userStatus?: string;
  lastModified?: string;
}

export type UserRole = 'admin' | 'hr_clerk' | 'manager' | 'candidate' | 'viewer';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export interface LoginCredentials {
  identifier: string; // Can be email or phone number
  password: string;
}

export interface RegisterData {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  temporaryPassword?: string;
}

export interface RegisterResponseData {
  message: string;
  user: {
    email: string;
    firstName: string;
    lastName:string;
    role: UserRole;
  };
  temporaryPassword?: string;
}

// Employee Record types
export interface EmployeeRecord {
  id: string;
  
  // Personal Information - Candidate Input
  fullNames: string; // Also Known As field
  alsoKnownAs?: string;
  surname: string;
  maidenName?: string;
  dateOfBirth: string;
  gender: 'Male' | 'Female';
  nationality: string;
  idNumber: string; // ID Number with numeric validation
  contactNumber: string; // Contact Number with phone format validation
  email?: string; // Email with email format validation - now optional
  taxNumber?: string; // Now optional
  socialSecurityNumber?: string; // Now optional
  
  // Address Information - Structured format
  residentialAddress: {
    streetAddress: string;
    townCity: string;
    customTownCity?: string; // For "Other" option
  };
  
  // Banking Details - Grouped Fields
  bankingDetails?: {
    bank: string;
    branch: string;
    accountNumber: string;
    accountType: string;
    accountName: string;
  };
  
  // Personal Status
  maritalStatus: 'Single' | 'Married';
  dependents: Dependent[]; // Repeater for multiple entries

  // Additional mandatory fields
  criminalRecords: 'Yes' | 'No';
  preExistingDisability: 'Yes' | 'No';
  disabilityDescription?: string;
  qualification: string;
  
  // Employment Information
  employmentStatus: 'Employed' | 'Unemployed'; // Dropdown for Y/N
  currentEmployer?: string;
  currentPosition?: string;
  positionAppliedFor: string[]; // Multiple select dropdown with position options
  previouslyEmployedByTrigon: 'Yes' | 'No';

  // Previous Trigon Employment Details (conditional fields)
  previousTrigonPosition?: string;
  previousTrigonEmployeeNumber?: string;
  

  
  // Employer Input Fields
  employeeNumber?: string; // Auto-generated or manual
  jobGrade?: string;
  jobTitle?: string;
  department?: string;
  
  // Profile Photo
  profilePhoto?: DocumentAttachment;
  
  // File Attachments with Document Types
  attachments: DocumentAttachment[];
  
  // System Fields
  status: EmployeeStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName?: string;
  updatedBy: string;
  updatedByName?: string;
  
  // Review and Notes
  reviewNotes?: string;
  
  // Review Progress
  reviewProgress?: {
    checklist: {
      personalDetails: boolean;
      residentialAddress: boolean;
      employmentStatus: boolean;
      documentsProvided: boolean;
    };
    lastSavedBy?: string;
    lastSavedAt?: string;
  };

  // Blacklist Information
  blacklistInfo?: {
    reason: string;
    blacklistedBy: string;
    blacklistedByName: string;
    blacklistedAt: string;
    notes?: string;
  };

  // Additional dynamic fields
  customFields?: Record<string, any>;
  

}

export interface Dependent {
  id: string;
  fullNames: string; // Full Names as per spec
  surname: string;
  maidenName?: string;
  idNumber?: string; // ID Number as per spec
  relation: string; // Relationship to employee (required)
}

export interface EmploymentHistoryEntry {
  id: string;
  companyName: string;
  position: string;
  startDate: string;
  endDate?: string;
  reasonForLeaving?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export type EmployeeStatus =
  | 'draft'
  | 'new_submission'
  | 'candidate'
  | 'under_review'
  | 'requested_rework'
  | 'profile_verified'
  | 'available_for_hire'
  | 'hired'
  | 'current_employee'
  | 'past_employee'
  | 'terminated'
  | 'blacklisted'
  | 'rejected';

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Updated type to match actual API response structure
export interface PaginatedApiResponse<T> extends ApiResponse<{
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}> {}

// Document Attachment types
export interface DocumentAttachment {
  id: string;
  fileName: string;
  fileType: string;
  documentType: DocumentType;
  fileKey: string;
  downloadUrl?: string;
  uploadedAt: string;
}

export type DocumentType = 
  | 'ID'
  | 'Birth Certificate'
  | 'Driver\'s License'
  | 'ITAS Registration Certificate'
  | 'Social Security Card'
  | 'Bank Confirmation Letter'
  | 'Police Clearance Certificate'
  | 'Proof of Address'
  | 'CV'
  | 'Qualifications'
  | 'Other Certifications';

// File Upload types
export interface FileUploadRequest {
  fileName: string;
  fileType: string;
  employeeId: string;
  documentType: DocumentType;
}

export interface PresignedUrlResponse {
  uploadUrl: string;
  fileKey: string;
  downloadUrl: string;
}

// Audit types
export interface AuditLog {
  employeeId: string;
  timestamp: string;
  userId: string;
  userName: string;
  action: 'create' | 'update' | 'delete' | 'status_change' | 'login' | 'logout' | 'user_create' | 'user_delete' | 'user_status_change' | 'password_reset' | 'password_reset_initiated' | 'password_reset_completed' | 'notification_sent' | 'field_flags_created' | 'field_flags_resolved' | 'review_progress_saved' | 'blacklist';
  fieldChanges: FieldChange[];
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  targetUserId?: string;
  targetUserName?: string;
  metadata?: Record<string, any>;
}

export interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

// Search and Filter types
export interface SearchFilters {
  status?: EmployeeStatus[];
  department?: string[];
  position?: string[];
  dateCreated?: {
    start?: string;
    end?: string;
  };
  searchTerm?: string;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

export interface QueryOptions {
  page?: number;
  limit?: number;
  filters?: SearchFilters;
  sort?: SortOptions;
}

// Export types
export interface ExportRequest {
  fields?: string[];
  filters?: {
    status?: EmployeeStatus[];
    department?: string[];
    dateRange?: {
      start: string;
      end: string;
    };
  };
  format: 'csv' | 'excel';
}

// Form validation types
export interface ValidationError {
  field: string;
  message: string;
}

export interface FormValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Field flagging types
export interface FieldFlag {
  fieldPath: string; // e.g., 'firstName', 'address.street', 'uploadedFiles.idDocument'
  message: string;   // Custom message explaining what's wrong
  severity?: 'error' | 'warning' | 'info'; // Optional severity level
}

// Notification types
export interface NotificationRequest {
  to: string;
  subject: string;
  template: 'submission_received' | 'rework_required' | 'welcome' | 'candidate_credentials' | 'custom' | 'draft_link';
  data: Record<string, any>;
  fieldFlags?: FieldFlag[]; // New field for specific field flagging
}

// UI specific types
export interface TabItem {
  id: string;
  label: string;
  icon?: any;
  badge?: string | number;
}

export interface NavItem {
  id: string;
  label: string;
  href: string;
  icon?: any;
  badge?: string | number;
  children?: NavItem[];
}

export interface StatusBadgeProps {
  status: EmployeeStatus;
  size?: 'sm' | 'md' | 'lg';
}

export interface TableColumn<T = any> {
  key: string;
  title: string;
  render?: (value: any, record: T, index: number) => any;
  sortable?: boolean;
  width?: string | number;
}

// Form types for React Hook Form
export interface EmployeeFormData extends Omit<EmployeeRecord, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'createdByName' | 'updatedByName'> {}

export interface AddressFormData {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface BankingFormData {
  bank: string;
  branch: string;
  accountNumber: string;
  accountType: string;
  accountName: string;
}

// Position management types
export interface Position {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

// Position options for dropdown (legacy - will be replaced by dynamic data)
export const POSITION_OPTIONS = [
  'General Worker',
  'Artisan',
  'Middle Management',
  'Senior Management',
  'Supervisor',
  'Technician',
  'Engineer',
  'Operator',
  'Other'
] as const;

export type PositionOption = typeof POSITION_OPTIONS[number];

// Locations options for dropdown - Official Namibian towns and cities
export const LOCATIONS_OPTIONS = [
  'Kombat',
  'Neu Sommerau',
  'Kavango location',
  'Arandis',
  'Aranos',
  'Eenhana',
  'Gobabis',
  'Grootfontein',
  'Helao Nafidi',
  'Henties Bay',
  'Karasburg',
  'Karibib',
  'Katima Mulilo',
  'Keetmanshoop',
  'Khorixas',
  'Lüderitz',
  'Mariental',
  'Nkurenkuru',
  'Okahandja',
  'Okakarara',
  'Okahao',
  'Omaruru',
  'Omuthiya',
  'Ondangwa',
  'Ongwediva',
  'Oniipa',
  'Opuwo',
  'Oranjemund',
  'Oshakati',
  'Oshikuku',
  'Otavi',
  'Otjiwarongo',
  'Outapi',
  'Outjo',
  'Rehoboth',
  'Ruacana',
  'Rundu',
  'Swakopmund',
  'Tsumeb',
  'Usakos',
  'Walvis Bay',
  'Windhoek',
  'Other'
] as const;

export type LocationsOption = typeof LOCATIONS_OPTIONS[number];

// Qualification options for dropdown
export const QUALIFICATION_OPTIONS = [
  'None',
  'Primary School',
  'High School',
  'Technical Certificate',
  'Diploma',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'Doctoral Degree',
  'Professional Certification',
  'Other'
] as const;

export type QualificationOption = typeof QUALIFICATION_OPTIONS[number];

// Relation options for dependents
export const RELATION_OPTIONS = [
  'Spouse',
  'Child',
  'Parent',
  'Sibling',
  'Grandparent',
  'Grandchild',
  'Other'
] as const;

export type RelationOption = typeof RELATION_OPTIONS[number];


// Configuration types
export interface AppConfig {
  apiUrl: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  s3BucketUrl: string;
  environment: 'development' | 'staging' | 'production';
} 
